/**
 * AssetContext - Centralized asset management from CMS
 * Preloads all assets once and provides them throughout the app
 * Supports: logos, hero images (per destination), placeholders, page-specific images
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchWithRetry } from '@/utils/fetchWithRetry';

const API_URL = window.location.origin;

// Comprehensive fallback assets (used when CMS is unavailable)
const FALLBACK_ASSETS = {
  // ===== BRANDING =====
  logo_primary: 'https://customer-assets.emergentagent.com/job_e561d373-9c90-4225-a893-129d3a1abc0e/artifacts/ricl3iso_Unixperts%20Logo.png',
  logo_white: 'https://customer-assets.emergentagent.com/job_e561d373-9c90-4225-a893-129d3a1abc0e/artifacts/ricl3iso_Unixperts%20Logo.png',
  
  // ===== GPTW CERTIFICATION BADGES =====
  gptw_badge_color: 'https://customer-assets.emergentagent.com/job_b2fca32b-0b1d-4c48-9bb2-97f9d4416095/artifacts/q41s94bq_Certification%20Badge%202025-2026.jpg',
  gptw_badge_black: 'https://customer-assets.emergentagent.com/job_b2fca32b-0b1d-4c48-9bb2-97f9d4416095/artifacts/8m6pw7hl_Certification%20Badge%202025-2026-05.png',
  gptw_badge_white: 'https://customer-assets.emergentagent.com/job_b2fca32b-0b1d-4c48-9bb2-97f9d4416095/artifacts/m1eusu8j_Certification%20Badge%202025-2026-06.png',
  
  // ===== HOMEPAGE HERO =====
  hero_home_desktop: 'https://static.prod-images.emergentagent.com/jobs/e5f78efe-5b96-4b62-ab9f-11b1c0606d71/images/1afa39692404bcf78e735905f91b371210e9dae24adb15b1ccd5f349f2442fa5.png',
  hero_home_mobile: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/b37ea8fd8a4cc556a1fe175a1caa0fe01eaec5cb85bbfa3c4938d465d8a045b4.png',
  
  // ===== DESTINATION HEROES - Desktop =====
  hero_usa_desktop: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/8987d0699afe7402f73b16854767f98f32ebfd00a18dfe847a37444db5353bd5.png',
  hero_canada_desktop: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/c1aa510683c445460131840d513e4f553c79a26742a00f3a471d4fab7e8793a2.png',
  hero_uk_desktop: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/6c8de25df10e898a9d3600b71e27425f9b4c560944b630df0b6c5e460794504a.png',
  hero_australia_desktop: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/485a7f5ddb2e7edd0dac78f0933d7e23f2ee733e4c56470188d7442e87381ff0.png',
  hero_germany_desktop: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/4819bad9a28616f54a185f58df90b442c70b8243a1b20006c59ea6650ecd722a.png',
  hero_ireland_desktop: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/4f26352ff923bc62bde468a7177e1dee7222bdf875cbf66f0bd5763f47e843f9.png',
  hero_new_zealand_desktop: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/4db479d6b299c9ae132e496187ea97b9b5553a71b71ec84491face60560542e3.png',
  hero_dubai_desktop: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/c5f8a0b3a3dbf23ba4c5a6b44f488a7e6885f7a28dc8e7cfb8fcdcbd468a39da.png',
  hero_france_desktop: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1920&q=80',
  hero_singapore_desktop: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=1920&q=80',
  hero_netherlands_desktop: 'https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=1920&q=80',
  hero_sweden_desktop: 'https://images.unsplash.com/photo-1590089415225-401ed6f9db8e?w=1920&q=80',
  
  // ===== DESTINATION HEROES - Mobile =====
  hero_usa_mobile: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/9d3e17aa2020c83ccc95868fe39e725492ff1e399754fa2ff8e7afb2325083c1.png',
  hero_canada_mobile: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/68d975916fd10fe4a39c86cb711911ba23c1c7e4b2d2363f79785f950106419c.png',
  hero_uk_mobile: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/ca684d55cf4628fb3f9db6bb8f4546a7be593055382f81f0300f4a9364d38a5d.png',
  hero_australia_mobile: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/bc2ae005bb759df373d79cae64c1304cc4d5231f2891574d0cec22292321d97a.png',
  hero_germany_mobile: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/be205fce2fa0d62a57f0ca393efc9654c3236b456a5bb7967b83eba7ecc8ebb4.png',
  hero_ireland_mobile: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/b40f199f8f81c61d7b47e7037adff03215303433aa9369314ad44531eecf9b58.png',
  hero_new_zealand_mobile: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/06644e21a4632d6a258d6ced61e772b10b6e2818f364b5c3eb00ec05cdbffde7.png',
  hero_dubai_mobile: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/c5f8a0b3a3dbf23ba4c5a6b44f488a7e6885f7a28dc8e7cfb8fcdcbd468a39da.png',
  hero_france_mobile: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800&q=80',
  hero_singapore_mobile: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=800&q=80',
  hero_netherlands_mobile: 'https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=800&q=80',
  hero_sweden_mobile: 'https://images.unsplash.com/photo-1590089415225-401ed6f9db8e?w=800&q=80',
  
  // ===== DESTINATION CARD IMAGES (for list pages) =====
  card_usa: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/8987d0699afe7402f73b16854767f98f32ebfd00a18dfe847a37444db5353bd5.png',
  card_canada: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/c1aa510683c445460131840d513e4f553c79a26742a00f3a471d4fab7e8793a2.png',
  card_uk: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/6c8de25df10e898a9d3600b71e27425f9b4c560944b630df0b6c5e460794504a.png',
  card_australia: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/485a7f5ddb2e7edd0dac78f0933d7e23f2ee733e4c56470188d7442e87381ff0.png',
  card_germany: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/4819bad9a28616f54a185f58df90b442c70b8243a1b20006c59ea6650ecd722a.png',
  card_ireland: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/4f26352ff923bc62bde468a7177e1dee7222bdf875cbf66f0bd5763f47e843f9.png',
  card_new_zealand: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/4db479d6b299c9ae132e496187ea97b9b5553a71b71ec84491face60560542e3.png',
  card_dubai: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/c5f8a0b3a3dbf23ba4c5a6b44f488a7e6885f7a28dc8e7cfb8fcdcbd468a39da.png',
  card_france: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800',
  card_singapore: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=800',
  card_netherlands: 'https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=800',
  card_sweden: 'https://images.unsplash.com/photo-1590089415225-401ed6f9db8e?w=800',
  
  // ===== OTHER PAGE HEROES =====
  hero_services: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1920',
  hero_about: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920',
  hero_contact: 'https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1920',
  hero_blog: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=1920',
  hero_events: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1920',
  hero_portal_login: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1920',
  hero_portal_dashboard: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1920',
  hero_success_stories: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1920',
  
  // ===== PLACEHOLDERS =====
  blog_placeholder: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=800',
  university_placeholder: 'https://images.unsplash.com/photo-1562774053-701939374585?w=600',
  event_placeholder: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800',
  team_member_placeholder: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400',
  testimonial_placeholder: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200',
  resource_placeholder: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=800',
  student_placeholder: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400',
  
  // ===== HOMEPAGE SECTIONS =====
  homepage_why_choose: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/b49ec939dcc99e8554de63ada9b2850e23d14f15de24b5d9d4abb64fd3f3aa9c.png',
  homepage_services: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/4a400db3708b85c206d4e700438c579ce94123ecdcb42fdcfe91e661db03a37b.png',
  homepage_cta: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/2235dcf9d6cbba7c8ada0df52dd30c647dbfc2eab8e2a909ad9e0e19166d7f3f.png',
  homepage_exploring: 'https://images.unsplash.com/photo-1562793440-ba6f231b3bd4?w=800',
  homepage_students_collab: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800',
  
  // ===== LIFE AT UNIXPERTS =====
  life_photo_01: 'https://customer-assets.emergentagent.com/job_b2fca32b-0b1d-4c48-9bb2-97f9d4416095/artifacts/1b0uuabv_F1.jpg',
  life_photo_02: 'https://customer-assets.emergentagent.com/job_b2fca32b-0b1d-4c48-9bb2-97f9d4416095/artifacts/aps4dspd_F2.jpg',
  life_photo_03: 'https://customer-assets.emergentagent.com/job_b2fca32b-0b1d-4c48-9bb2-97f9d4416095/artifacts/76t8kziv_F3.jpg',
  life_photo_04: 'https://customer-assets.emergentagent.com/job_b2fca32b-0b1d-4c48-9bb2-97f9d4416095/artifacts/oeywl63a_F4.jpg',
  life_photo_05: 'https://customer-assets.emergentagent.com/job_b2fca32b-0b1d-4c48-9bb2-97f9d4416095/artifacts/7cgm0qqr_F5.jpg',
  life_photo_06: 'https://customer-assets.emergentagent.com/job_b2fca32b-0b1d-4c48-9bb2-97f9d4416095/artifacts/tdudwu10_F6.jpg',
  life_photo_07: 'https://customer-assets.emergentagent.com/job_b2fca32b-0b1d-4c48-9bb2-97f9d4416095/artifacts/3xnpyqot_F7.jpg',
  life_photo_08: 'https://customer-assets.emergentagent.com/job_b2fca32b-0b1d-4c48-9bb2-97f9d4416095/artifacts/mpblcegy_F8.jpg',
  life_photo_09: 'https://customer-assets.emergentagent.com/job_b2fca32b-0b1d-4c48-9bb2-97f9d4416095/artifacts/2naoh83i_F10.jpg',
  life_photo_10: 'https://customer-assets.emergentagent.com/job_b2fca32b-0b1d-4c48-9bb2-97f9d4416095/artifacts/3cd9xhx7_F11.jpg',
  life_photo_11: 'https://customer-assets.emergentagent.com/job_b2fca32b-0b1d-4c48-9bb2-97f9d4416095/artifacts/s6ekxtau_F12.jpg',
  life_photo_12: 'https://customer-assets.emergentagent.com/job_b2fca32b-0b1d-4c48-9bb2-97f9d4416095/artifacts/973bc5vt_F13.jpg',
  life_photo_13: 'https://customer-assets.emergentagent.com/job_b2fca32b-0b1d-4c48-9bb2-97f9d4416095/artifacts/fe9pxy6p_F14.jpg',
  life_photo_14: 'https://customer-assets.emergentagent.com/job_b2fca32b-0b1d-4c48-9bb2-97f9d4416095/artifacts/zv2j74ds_F15.jpg',
};

// Destination slug to asset key mapping
const DESTINATION_SLUG_MAP = {
  'usa': 'usa',
  'united-states': 'usa',
  'canada': 'canada',
  'uk': 'uk',
  'united-kingdom': 'uk',
  'australia': 'australia',
  'germany': 'germany',
  'ireland': 'ireland',
  'new-zealand': 'new_zealand',
  'newzealand': 'new_zealand',
  'dubai': 'dubai',
  'uae': 'dubai',
  'france': 'france',
  'singapore': 'singapore',
  'netherlands': 'netherlands',
  'sweden': 'sweden',
};

const AssetContext = createContext(null);

export const AssetProvider = ({ children }) => {
  const [assets, setAssets] = useState(FALLBACK_ASSETS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all assets from CMS on mount
  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const response = await fetchWithRetry(`${API_URL}/api/assets/`, {}, { label: 'assets' });
        if (response && response.ok) {
          const data = await response.json();
          // Convert array to object keyed by asset_id
          const assetMap = {};
          (data.assets || []).forEach(asset => {
            const key = asset.asset_key || asset.asset_id;
            if (key && asset.url) {
              assetMap[key] = asset.url;
            }
          });
          // Merge with fallbacks (CMS values override fallbacks)
          setAssets(prev => ({ ...prev, ...assetMap }));
        }
      } catch (err) {
        // fetchWithRetry already logged a warning; keep using fallback assets
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAssets();
  }, []);

  // Get asset URL by key with fallback
  const getAsset = useCallback((key, fallback = '') => {
    return assets[key] || fallback || FALLBACK_ASSETS[key] || '';
  }, [assets]);

  // Get logo with variant support
  const getLogo = useCallback((variant = 'primary') => {
    const key = variant === 'white' ? 'logo_white' : 'logo_primary';
    return getAsset(key);
  }, [getAsset]);

  /**
   * Get hero image for a destination page
   * @param {string} destination - Destination name or slug (e.g., 'usa', 'united-states', 'UK')
   * @param {boolean} isMobile - Whether to get mobile or desktop version
   * @returns {string} Hero image URL
   */
  const getDestinationHero = useCallback((destination, isMobile = false) => {
    // Normalize the destination to a standard key
    const normalizedDest = destination.toLowerCase().replace(/[\s-]+/g, '-');
    const assetKey = DESTINATION_SLUG_MAP[normalizedDest] || normalizedDest.replace(/-/g, '_');
    
    const suffix = isMobile ? '_mobile' : '_desktop';
    const key = `hero_${assetKey}${suffix}`;
    
    // Try to get the asset, fall back to desktop version if mobile not found
    let result = getAsset(key);
    if (!result && isMobile) {
      result = getAsset(`hero_${assetKey}_desktop`);
    }
    return result;
  }, [getAsset]);

  /**
   * Get card image for destination list/grid displays
   * @param {string} destination - Destination name or slug
   * @returns {string} Card image URL
   */
  const getDestinationCard = useCallback((destination) => {
    const normalizedDest = destination.toLowerCase().replace(/[\s-]+/g, '-');
    const assetKey = DESTINATION_SLUG_MAP[normalizedDest] || normalizedDest.replace(/-/g, '_');
    const key = `card_${assetKey}`;
    
    // Fall back to hero desktop image if card not found
    return getAsset(key) || getAsset(`hero_${assetKey}_desktop`);
  }, [getAsset]);

  /**
   * Get placeholder image by type
   * @param {string} type - Type of placeholder (blog, university, event, team_member, testimonial, resource, student)
   * @returns {string} Placeholder image URL
   */
  const getPlaceholder = useCallback((type) => {
    return getAsset(`${type}_placeholder`);
  }, [getAsset]);

  /**
   * Get hero image for a specific page
   * @param {string} page - Page name (services, about, contact, blog, events, portal_login, success_stories)
   * @returns {string} Hero image URL
   */
  const getPageHero = useCallback((page) => {
    const key = `hero_${page.toLowerCase().replace(/[\s-]+/g, '_')}`;
    return getAsset(key);
  }, [getAsset]);

  /**
   * Get homepage section image
   * @param {string} section - Section name (why_choose, services, cta, exploring, students_collab)
   * @returns {string} Section image URL
   */
  const getHomepageSection = useCallback((section) => {
    const key = `homepage_${section.toLowerCase().replace(/[\s-]+/g, '_')}`;
    return getAsset(key);
  }, [getAsset]);

  /**
   * Get GPTW badge based on context
   * @param {string} variant - 'color' (default), 'black' (for light backgrounds), 'white' (for dark backgrounds)
   * @returns {string} GPTW badge URL
   */
  const getGPTWBadge = useCallback((variant = 'color') => {
    const key = `gptw_badge_${variant}`;
    return getAsset(key);
  }, [getAsset]);

  /**
   * Check if assets are still loading
   */
  const isLoading = loading;

  const value = {
    assets,
    loading,
    isLoading,
    error,
    getAsset,
    getLogo,
    getDestinationHero,
    getDestinationCard,
    getPlaceholder,
    getPageHero,
    getHomepageSection,
    getGPTWBadge,
    FALLBACK_ASSETS
  };

  return (
    <AssetContext.Provider value={value}>
      {children}
    </AssetContext.Provider>
  );
};

// Hook to use assets
export const useAssetContext = () => {
  const context = useContext(AssetContext);
  if (!context) {
    throw new Error('useAssetContext must be used within an AssetProvider');
  }
  return context;
};

// Export fallback for direct use where context isn't available
export { FALLBACK_ASSETS, DESTINATION_SLUG_MAP };

export default AssetContext;
